import useTableState from "@/components/Table/hooks/useTableState";
import Table from "@/components/Table/Table";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { ProductBy, useProductImpactCategories } from "@/pages/dashboard/hooks/useProductImpact";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import { IImpact, ImpactMetric } from "@/pages/dashboard/utils/types";
import { useEffect } from "react";
import { FacilityImpactDetailMode } from "./CategoryImpactSideBySide";

export default function CategoryImpactPerLifeCycle({
  mode,
  by,
  metric,
}: {
  mode: FacilityImpactDetailMode;
  by: ProductBy;
  metric: ImpactMetric;
}) {
  const { pagination, sort, search, filter, onSearchFilterSortPaginate, setPagination } =
    useTableState({ defaultPagination: { page: 1, pageSize: 10 } });
  const { startDate, endDate } = useDateRange();

  useEffect(() => {
    setPagination({ ...pagination, page: 1 });
  }, [startDate, endDate]);

  const impactFormatter = (valueImpact?: IImpact, totalImpact?: IImpact) => {
    const total = totalImpact?.[metric.key];
    const value = valueImpact?.[metric.key];

    if (mode === "Absolute") {
      return metric.formatter(value || 0);
    } else {
      return total ? formatPercentage((value || 0) / total) : NaN;
    }
  };

  const { data, isLoading, isFetching } = useProductImpactCategories({
    by,
    pagination,
    filter,
    search,
    sort,
    metric: metric.key,
  });

  return (
    <Table
      filter={filter}
      hideRaster={true}
      type={"grey"}
      sort={sort}
      search={search}
      columns={[
        {
          label: "Category",
          key: "category",
          searchable: true,
          sortable: true,
          renderer: (record) => record.category || "No category",
        },
        {
          label: "Raw material",
          key: `material_${metric.key}`,
          renderer: (record) =>
            impactFormatter(record.impact.material, data?.total_impact.material),
          sortable: true,
        },
        {
          label: "Packaging",
          key: `packaging_${metric.key}`,
          renderer: (record) =>
            impactFormatter(record.impact.packaging, data?.total_impact.packaging),
          sortable: true,
        },
        {
          label: "Production",
          key: `production_${metric.key}`,
          renderer: (record) =>
            impactFormatter(record.impact.production, data?.total_impact.production),
          sortable: true,
        },
        {
          label: "Transport",
          key: `transport_${metric.key}`,
          renderer: (record) =>
            impactFormatter(record.impact.transport, data?.total_impact.transport),
          sortable: true,
        },
        {
          label: "Usage",
          key: `usage_${metric.key}`,
          renderer: (record) => impactFormatter(record.impact.usage, data?.total_impact.usage),
          sortable: true,
        },
        {
          label: "End of life",
          key: `eol_${metric.key}`,
          renderer: (record) => impactFormatter(record.impact.eol, data?.total_impact.eol),
          sortable: true,
        },
        {
          key: `product_${metric.key}`,
          label: metric.label,
          sortable: true,
          renderer: (record) => impactFormatter(record.impact.product, data?.total_impact.product),
        },
      ]}
      data={data?.data}
      loading={isLoading || isFetching}
      onSearchFilterSortPaginate={onSearchFilterSortPaginate}
      pagination={pagination}
      rowKey={"key"}
    />
  );
}
